import React, { useState, useEffect } from 'react'
import { Modal, Breadcrumb, message } from 'antd'
import { Picker } from 'antd-mobile'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import type { TravelCategory, ExpenseType, TravelCompanion } from '@/types'
import type {
  TravelFormState,
  FormCompanion,
  FormTripSegment,
  SavedDataSnapshot,
  CreateDraftRequest,
  CompanionRequest,
  TripSegmentRequest,
  TravelCompanionResponse,
  TripSegmentResponse,
  FormUpdateParams,
  OccupiedPeriod,
} from '@/types/travel'
import TravelCategorySelector from '@/components/TravelApplication/TravelCategorySelectorResponsive'
import ExpenseTypeSelector from '@/components/TravelApplication/ExpenseTypeSelectorResponsive'
import LocalTripForm from '@/components/TravelApplication/LocalTripForm'
import CrossCityTripForm from '@/components/TravelApplication/CrossCityTripForm'
import CompanionList from '@/components/TravelApplication/CompanionList'
import ApprovalPreview from '@/components/TravelApplication/ApprovalPreview'
import CtripGuideText from '@/components/TravelApplication/CtripGuideText'
import BasicInfoSection, { BasicInfo as BasicInfoType } from '@/components/TravelApplication/BasicInfoSection'
import { apiClient } from '@/services/api'
// 新增代码 - T117 集成新组件和 Hook
import SubmitConfirmDialog from '@/components/TravelApplication/SubmitConfirmDialog'
import type { SubmitSummary } from '@/components/TravelApplication/SubmitConfirmDialog'
import useApprovalPreview from './hooks/useApprovalPreview'
import useAutoSave from './hooks/useAutoSave'
import type { LocalTripFormData, CrossCityTripFormData } from '@/types/forms'
import { validateCompanions, validateDestinationCities } from '@/utils/validation'
import { useIsMobile } from '@/hooks/useIsMobile'
import { loadNoBudgetDepartments } from '@/services/noBudgetDepartmentCache'

interface TravelApplicationFormProps {
  mode?: 'create' | 'edit' | 'view';
}

/**
 * 将后端返回的日期字段转为前端 ISO 字符串（"YYYY-MM-DDTHH:mm"）
 * 兼容两种格式：
 *   - 数组格式 [2026, 3, 2, 9, 0]（spring.jackson.write-dates-as-timestamps=true 时）
 *   - 字符串格式 "2026-03-02T09:00:00"（write-dates-as-timestamps=false 时）
 */
const parseDateFromBackend = (date: unknown): string => {
  if (!date) return ''
  if (typeof date === 'string') {
    // 截取到分钟精度，与 DateTimePicker 期望的 "YYYY-MM-DDTHH:mm" 对齐
    return date.substring(0, 16)
  }
  if (Array.isArray(date) && date.length >= 3) {
    const [y, m, d, h = 0, min = 0] = date as number[]
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
  }
  return ''
}

/**
 * 跨城出差时长本地计算：(dayDiff + 1) × 8
 * 与后端百川路由变量完全对齐，不依赖考勤排班。
 */
function calcCrossCityHours(startDate: string, endDate: string): number {
  const startDay = new Date(startDate.substring(0, 10))
  const endDay   = new Date(endDate.substring(0, 10))
  const dayDiff  = Math.round((endDay.getTime() - startDay.getTime()) / 86400000)
  return (dayDiff + 1) * 8
}

/** 表单状态初始值 */
const INITIAL_FORM_STATE: TravelFormState = {
  category: null,
  expenseType: 'DEPARTMENT',
  startDate: '',
  endDate: '',
  reason: '',
  location: '',
  departureCity: '',
  departureCityCode: '',
  destinationCity: '',
  destinationCityCode: '',
  destinationCities: [],
  companions: [],
  hasCompanion: false,
  estimatedExpense: null,
  showInLingxi: true,
  multiSegment: false,
  tripSegments: [],
}

/**
 * 差旅申请表单页面
 * 支持同城和跨城申请
 *
 * @param mode - 表单模式：create（新建）、edit（编辑）、view（查看）
 */
const TravelApplicationForm: React.FC<TravelApplicationFormProps> = ({ mode = 'create' }) => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [hasCompanionPickerVisible, setHasCompanionPickerVisible] = useState(false)
  const { id: applicationId } = useParams<{ id: string }>()  // 获取URL参数中的申请ID
  const [category, setCategory] = useState<TravelCategory | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [savedDraftId, setSavedDraftId] = useState<string | number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  /** 加载后记录申请状态和审批意见，用于在编辑/查看页顶部展示 */
  const [loadedStatus, setLoadedStatus] = useState<string | null>(null)
  const [loadedApprovalOpinion, setLoadedApprovalOpinion] = useState<string | null>(null)
  /** 已启动流程的真实审批链（APPROVING/APPROVED/REJECTED 等状态下使用） */
  const [processChainNodes, setProcessChainNodes] = useState<import('@/types').ApprovalFlowNode[]>([])
  const [isProcessChainLoading, setIsProcessChainLoading] = useState(false)

  // 追踪上次保存的数据，用于检测数据变化
  const [, setLastSavedData] = useState<SavedDataSnapshot | null>(null)

  // 同行人验证状态
  const [companionValidation, setCompanionValidation] = useState<{
    isValid: boolean
    message?: string
  }>({ isValid: true })

  // Phase 3.1 — 逻辑0：占用时段（用于结束时间选择器置灰）
  const [occupiedRanges, setOccupiedRanges] = useState<OccupiedPeriod[]>([])
  // Phase 3.1 — 逻辑1：排班校验状态（undefined=未检验，true=有排班，false=无排班）
  const [hasScheduling, setHasScheduling] = useState<boolean | undefined>(undefined)
  // 结束日期排班校验状态（跨天时使用，undefined=同天或未检验，false=结束日期无排班）
  const [endDateHasScheduling, setEndDateHasScheduling] = useState<boolean | undefined>(undefined)
  // Phase 3.1 — 逻辑2：出差时长（小时）
  const [businessLength, setBusinessLength] = useState<number | undefined>(undefined)

  // 基础信息状态
  const [basicInfo, setBasicInfo] = useState<BasicInfoType>({
    applicantId: '',
    applicantName: '',
    applicantDisplayNumber: '',
    applicantDeptId: '',
    applicantDepartment: '',
    applicantCollection: undefined,
  })

  // 新建模式：自动带出当前登录人及其部门
  useEffect(() => {
    if (mode !== 'create') return
    const autoFillApplicant = async () => {
      try {
        const [me, noBudgetIds] = await Promise.all([
          apiClient.getCurrentUser(),
          loadNoBudgetDepartments(),
        ])
        if (!me.userId) return

        // 默认部门命中无差旅预算黑名单 → 不自动回填部门字段，提示用户手动选择
        if (me.department && noBudgetIds.has(me.department)) {
          message.info('您的默认部门暂无差旅预算，请手动选择费用承担部门')
          setBasicInfo({
            applicantId: me.userId,
            applicantName: me.userName,
            applicantDisplayNumber: me.displayNumber || '',
            applicantDeptId: '',
            applicantDepartment: '',
            applicantCollection: me.collection,
          })
          return
        }

        // /me 接口已返回完整字段（displayNumber、department、collection），直接使用
        let deptName = me.departmentName || ''
        // 如有部门编码但无名称，查一次部门名
        if (me.department && !deptName) {
          try {
            const depts = await apiClient.searchDepartments(me.department, me.collection)
            const dept = depts.find(d => d.number === me.department) || depts[0]
            if (dept) deptName = dept.name || dept.shortName || dept.number || ''
          } catch {
            // 降级忽略
          }
        }

        setBasicInfo({
          applicantId: me.userId,
          applicantName: me.userName,
          applicantDisplayNumber: me.displayNumber || '',
          applicantDeptId: me.department || '',
          applicantDepartment: deptName,
          applicantCollection: me.collection,
        })
      } catch {
        // 查询失败不阻断，用户可手动选择
      }
    }
    autoFillApplicant()
  }, [mode])

  // 表单数据 - 使用精确类型替代 any
  const [formData, setFormData] = useState<TravelFormState>(INITIAL_FORM_STATE)

  // 新增代码 - 提交确认对话框状态
  const [showSubmitDialog, setShowSubmitDialog] = useState(false)

  // 新增代码 - 预算检查 Hook (FR-008, FR-009, FR-009A) - 暂时禁用
  // const {
  //   budgetResults,
  //   isChecking: isBudgetChecking,
  //   isExceeded: isBudgetExceeded,
  //   checkBudgetForSubmit,
  // } = useBudgetCheck({
  //   category,
  //   expenseType: formData.expenseType,
  // })

  // 计算审批预览使用的开始/结束时间（多段行程需要从 tripSegments 计算）
  const approvalPreviewDates = React.useMemo(() => {
    if (category === 'CROSS_CITY' && formData.multiSegment && formData.tripSegments && formData.tripSegments.length > 0) {
      // 多段行程：使用第一段的开始时间和最后一段的结束时间
      const firstSegment = formData.tripSegments[0]
      const lastSegment = formData.tripSegments[formData.tripSegments.length - 1]
      return {
        startDate: firstSegment.startDate || undefined,
        endDate: lastSegment.endDate || undefined,
      }
    }
    // 单段行程或同城：直接使用 formData 的时间
    return {
      startDate: formData.startDate || undefined,
      endDate: formData.endDate || undefined,
    }
  }, [category, formData.multiSegment, formData.tripSegments, formData.startDate, formData.endDate])

  const {
    nodes: approvalNodes,
    isLoading: isApprovalLoading,
    error: approvalError,
    refresh: _refreshApproval,
  } = useApprovalPreview({
    applicationId: savedDraftId,
    applicantId: basicInfo.applicantId,
    applicantName: basicInfo.applicantName,
    companions: (formData.companions || []).map(c => ({
      employeeId: c.nameId || '',
      name: c.name,
    })),
    startDate: approvalPreviewDates.startDate,
    endDate: approvalPreviewDates.endDate,
    category: category,
  })

  // T032: 申请人变化时拉取占用时段，并重置/重新触发排班校验与时长计算
  // 说明：排班校验（hasScheduling）和时长（businessLength）均与申请人强绑定，
  // 切换申请人必须清除旧人的校验结果；若此时 startDate 已有值，立即用新申请人重新校验。
  useEffect(() => {
    if (category !== 'LOCAL' || !basicInfo.applicantId) {
      setOccupiedRanges([])
      setHasScheduling(undefined)
      setEndDateHasScheduling(undefined)
      setBusinessLength(undefined)
      return
    }

    // 拉取新申请人的已占用时段
    apiClient
      .getOccupiedPeriods(basicInfo.applicantId)
      .then(setOccupiedRanges)
      .catch(() => setOccupiedRanges([]))

    // 重置旧人的校验结果，避免展示上一个申请人的结论
    setHasScheduling(undefined)
    setEndDateHasScheduling(undefined)
    setBusinessLength(undefined)

    // 如果已有开始时间，立即用新申请人重新做排班校验
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const currentStartDate = formData.startDate
    if (currentStartDate) {
      const dateOnly = currentStartDate.substring(0, 10)
      apiClient
        .checkScheduling(basicInfo.applicantId, dateOnly)
        .then((resp) => setHasScheduling(resp.data?.hasScheduling ?? false))
        .catch(() => setHasScheduling(undefined))

      // 如果结束时间也已选，重新计算出差时长，跨天时额外校验结束日期排班
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const currentEndDate = formData.endDate
      if (currentEndDate) {
        apiClient
          .calculateHours({
            employeeId: basicInfo.applicantId,
            holidayType: 'L13',
            startTime: currentStartDate.replace('T', ' '),
            endTime: currentEndDate.replace('T', ' '),
          })
          .then((resp) => setBusinessLength(resp.data?.businessLength))
          .catch(() => setBusinessLength(undefined))

        const startDateOnly = currentStartDate.substring(0, 10)
        const endDateOnly = currentEndDate.substring(0, 10)
        if (startDateOnly !== endDateOnly) {
          apiClient
            .checkScheduling(basicInfo.applicantId, endDateOnly)
            .then((resp) => setEndDateHasScheduling(resp.data?.hasScheduling ?? false))
            .catch(() => setEndDateHasScheduling(undefined))
        }
      }
    }
  // formData.startDate / formData.endDate 有意不加入依赖：
  // 仅在申请人切换时执行一次，避免与 handleFormChange 中的日期变化逻辑重复触发。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicInfo.applicantId, category])

  // T033: 编辑模式补充触发（M4修复：excludeId 因 applicantId 未变化而不生效的问题）
  useEffect(() => {
    if (category !== 'LOCAL' || !basicInfo.applicantId || !savedDraftId || mode !== 'edit') return
    apiClient
      .getOccupiedPeriods(basicInfo.applicantId, savedDraftId)
      .then(setOccupiedRanges)
      .catch(() => setOccupiedRanges([]))
  }, [savedDraftId, mode, basicInfo.applicantId, category])

  const handleCategoryChange = (selectedCategory: TravelCategory) => {
    // 如果当前没有选择类别，直接设置
    if (!category) {
      setCategory(selectedCategory)
      setFormData({
        ...formData,
        category: selectedCategory,
        // 同城不展示出差类型，固定为 DEPARTMENT
        ...(selectedCategory === 'LOCAL' ? { expenseType: 'DEPARTMENT' } : {}),
      })
      return
    }

    // 如果切换到相同类别，不做处理
    if (category === selectedCategory) {
      return
    }

    // 检查是否有已填写的数据
    const hasFilledData =
      formData.startDate ||
      formData.endDate ||
      formData.reason ||
      formData.location ||
      formData.departureCity ||
      formData.destinationCity ||
      (formData.destinationCities && formData.destinationCities.length > 0) ||
      (formData.estimatedExpense ?? 0) > 0 ||
      (formData.companions && formData.companions.length > 0) ||
      (formData.tripSegments && formData.tripSegments.length > 0)

    // 如果有已填写的数据，弹出确认对话框
    if (hasFilledData) {
      Modal.confirm({
        title: '确认切换出差类型？',
        icon: <ExclamationCircleOutlined />,
        content: '切换出差类型会清空当前已填写的内容，是否确认切换？',
        okText: '确认切换',
        cancelText: '取消',
        onOk() {
          // 清空表单数据并切换类别，但保留 version 和 id
          setCategory(selectedCategory)
          setFormData({
            ...INITIAL_FORM_STATE,
            category: selectedCategory,
            // 同城不展示出差类型，固定为 DEPARTMENT
            expenseType: selectedCategory === 'LOCAL' ? 'DEPARTMENT' : INITIAL_FORM_STATE.expenseType,
          })
        },
        onCancel() {
          // 取消切换，保持原类别
          // 不做任何操作
        },
      })
    } else {
      // 没有填写数据，直接切换
      setCategory(selectedCategory)
      setFormData({
        ...formData,
        category: selectedCategory,
        // 同城不展示出差类型，固定为 DEPARTMENT
        expenseType: selectedCategory === 'LOCAL' ? 'DEPARTMENT' : formData.expenseType,
      })
    }
  }

  const handleFormChange = (updates: FormUpdateParams) => {
    // 先更新表单数据（updater 保持纯函数，不含副作用）
    setFormData((prev) => ({ ...prev, ...updates }))

    // T041 逻辑1：同城模式下开始时间变化时，异步调用排班校验
    // 使用 formData（当前渲染帧的值）代替 prev，逻辑等价，但副作用在 updater 外部执行
    if (
      category === 'LOCAL' &&
      basicInfo.applicantId &&
      updates.startDate !== undefined &&
      updates.startDate !== formData.startDate
    ) {
      const newStartDate = updates.startDate
      if (newStartDate) {
        const dateOnly = newStartDate.substring(0, 10)
        setHasScheduling(undefined)
        apiClient
          .checkScheduling(basicInfo.applicantId, dateOnly)
          .then((resp) => setHasScheduling(resp.data?.hasScheduling ?? false))
          .catch(() => setHasScheduling(undefined))
      } else {
        setHasScheduling(undefined)
      }
      setBusinessLength(undefined)
      setEndDateHasScheduling(undefined)
    }

    // T042 逻辑2：同城模式下开始/结束时间均选定后查询时长
    if (category === 'LOCAL' && basicInfo.applicantId) {
      const startDate = updates.startDate !== undefined ? updates.startDate : formData.startDate
      const endDate = updates.endDate !== undefined ? updates.endDate : formData.endDate
      if (
        startDate && endDate &&
        (updates.startDate !== undefined || updates.endDate !== undefined)
      ) {
        apiClient
          .calculateHours({
            employeeId: basicInfo.applicantId,
            holidayType: 'L13',
            startTime: startDate.replace('T', ' '),
            endTime: endDate.replace('T', ' '),
          })
          .then((resp) => setBusinessLength(resp.data?.businessLength))
          .catch(() => setBusinessLength(undefined))

        // 跨天时额外校验结束日期是否有排班
        const startDateOnly = startDate.substring(0, 10)
        const endDateOnly = endDate.substring(0, 10)
        if (startDateOnly !== endDateOnly) {
          apiClient
            .checkScheduling(basicInfo.applicantId, endDateOnly)
            .then((resp) => setEndDateHasScheduling(resp.data?.hasScheduling ?? false))
            .catch(() => setEndDateHasScheduling(undefined))
        } else {
          setEndDateHasScheduling(undefined)
        }
      }
    }

    // 跨城模式下开始/结束时间均选定后查询时长
    if (category === 'CROSS_CITY' && basicInfo.applicantId) {
      const startDate = updates.startDate !== undefined ? updates.startDate : formData.startDate
      const endDate = updates.endDate !== undefined ? updates.endDate : formData.endDate
      const multiSegment = updates.multiSegment !== undefined ? updates.multiSegment : formData.multiSegment
      const tripSegments = updates.tripSegments !== undefined ? updates.tripSegments : formData.tripSegments

      // 单段行程：开始/结束时间均选定后本地计算
      if (!multiSegment && startDate && endDate &&
          (updates.startDate !== undefined || updates.endDate !== undefined)) {
        setBusinessLength(calcCrossCityHours(startDate, endDate))
      }
      // 多段行程：所有段的时间都选定后，分段计算并求和
      else if (multiSegment && tripSegments && tripSegments.length > 0) {
        const allSegmentsHaveTime = tripSegments.every(seg => seg.startDate && seg.endDate)
        if (allSegmentsHaveTime && (updates.tripSegments !== undefined || updates.startDate !== undefined || updates.endDate !== undefined)) {
          const totalLength = tripSegments.reduce((sum, seg) => {
            return sum + calcCrossCityHours(seg.startDate!, seg.endDate!)
          }, 0)
          setBusinessLength(totalLength)
        } else {
          setBusinessLength(undefined)
        }
      } else {
        setBusinessLength(undefined)
      }
    }
  }

  /**
   * 转换日期格式：确保格式为 ISO 8601 (YYYY-MM-DDTHH:mm)
   * 🔑 改名为 formatDateToISO，保留时间信息
   */
  const formatDateToISO = (dateStr: string): string => {
    if (!dateStr) return ''
    
    // 如果已经是 ISO 8601 格式 (YYYY-MM-DDTHH:mm 或更长)，直接返回
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dateStr)) {
      // 截取到分钟（去掉秒和毫秒）
      return dateStr.substring(0, 16)  // "YYYY-MM-DDTHH:mm"
    }
    
    // 如果只有日期部分 (YYYY-MM-DD)，添加默认时间 09:00
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return `${dateStr}T09:00`
    }
    
    // 其他格式，尝试截取或默认返回
    return dateStr.split('T')[0] + 'T09:00'
  }

  /**
   * 处理同行人验证状态变化
   */
  const handleCompanionValidationChange = (isValid: boolean, message?: string) => {
    setCompanionValidation({ isValid, message })
  }

  /**
   * 记录已保存的数据快照
   * 在保存草稿成功后调用
   */
  const recordSavedData = () => {
    setLastSavedData({
      category: formData.category,
      startDate: formData.startDate,
      endDate: formData.endDate,
      companions: formData.companions,
      tripSegments: formData.tripSegments,
      reason: formData.reason,
      location: formData.location,
      departureCity: formData.departureCity,
      destinationCity: formData.destinationCity,
      destinationCities: formData.destinationCities,
      estimatedExpense: formData.estimatedExpense ?? 0,
    })
  }

  /**
   * 草稿保存轻量校验：只要申请人和出差类别已选就允许保存
   * 不要求全部必填字段，避免用户填写过程中数据丢失
   */
  const canSaveDraft = (): boolean => {
    return !!(basicInfo.applicantId && basicInfo.applicantDeptId && category)
  }

  /**
   * 验证表单数据
   */
  const validateForm = (): { valid: boolean; message?: string } => {
    // 验证基础信息
    if (!basicInfo.applicantId || !basicInfo.applicantName) {
      return { valid: false, message: '请从下拉列表中选择申请人（手动输入无效）' }
    }
    if (!basicInfo.applicantDeptId || !basicInfo.applicantDepartment) {
      return { valid: false, message: '请从下拉列表中选择费用承担部门（手动输入无效）' }
    }

    // 验证出差类别
    if (!category) {
      return { valid: false, message: '请选择出差类别' }
    }

    // 根据类别验证不同字段
    if (category === 'LOCAL') {
      if (!formData.startDate || !formData.endDate) {
        return { valid: false, message: '请选择出差日期' }
      }
      if (!formData.location) {
        return { valid: false, message: '请填写外出地点' }
      }
      if (!formData.reason) {
        return { valid: false, message: '请填写出差事由' }
      }
      if (endDateHasScheduling === false) {
        return { valid: false, message: '不在排班期，无法提交申请，请修改外出时间在排班时间内，如仍需外出，请与HRBP确认排班。' }
      }
      if (businessLength !== undefined && businessLength === 0) {
        return { valid: false, message: '不在排班期，无法提交申请，请修改外出时间在排班时间内，如仍需外出，请与HRBP确认排班。' }
      }
    } else if (category === 'CROSS_CITY') {
      if (formData.multiSegment) {
        // 多段行程
        if (!formData.tripSegments || formData.tripSegments.length === 0) {
          return { valid: false, message: '请添加至少一个行程段' }
        }
        // 验证每个行程段的必填字段
        for (let i = 0; i < formData.tripSegments.length; i++) {
          const segment = formData.tripSegments[i]
          if (!segment.startDate || !segment.endDate) {
            return { valid: false, message: `行程段${i + 1}: 请填写开始和结束日期` }
          }
          if (!segment.departureCity || !segment.arrivalCity) {
            return { valid: false, message: `行程段${i + 1}: 请从下拉列表中选择出发和到达城市（手动输入无效）` }
          }
          if (segment.departureCity === segment.arrivalCity) {
            return { valid: false, message: `行程段${i + 1}: 出发城市与到达城市不能相同` }
          }
          if (!segment.purpose || segment.purpose.trim() === '') {
            return { valid: false, message: `行程段${i + 1}: 请填写本段事由` }
          }
        }
      } else {
        // 单行程
        if (!formData.startDate || !formData.endDate) {
          return { valid: false, message: '请选择出差日期' }
        }
        if (!formData.departureCity) {
          return { valid: false, message: '请从下拉列表中选择出发城市（手动输入无效）' }
        }
        // 多目的城市校验
        const destCities = formData.destinationCities
        if (destCities && destCities.length > 0) {
          const destValidation = validateDestinationCities(destCities, formData.departureCity)
          if (!destValidation.isValid) {
            return { valid: false, message: destValidation.message }
          }
        } else if (!formData.destinationCity) {
          // 存量数据兼容：destinationCities 为空时降级校验 destinationCity
          return { valid: false, message: '请从下拉列表中选择目的城市（手动输入无效）' }
        }
        if (!formData.reason) {
          return { valid: false, message: '请填写出差事由' }
        }
        // 时间段内无排班时拦截：businessLength === 0 表示已计算但无排班工时
        if (businessLength !== undefined && businessLength === 0) {
          return { valid: false, message: '不在排班期，无法提交申请，请修改外出时间在排班时间内，如仍需外出，请与HRBP确认排班。' }
        }
      }
      if (formData.expenseType !== 'SELF_PAID' && (formData.estimatedExpense == null)) {
        return { valid: false, message: '请填写预估费用' }
      }
    }

    // 验证同行人（如果有选择同行人）
    if (formData.hasCompanion) {
      // 检查是否至少添加了一个同行人
      if (!formData.companions || formData.companions.length === 0) {
        return { valid: false, message: '请添加至少一个同行人' }
      }
      // 检查同行人数量上限
      if (formData.companions.length > 40) {
        return { valid: false, message: '同行人数量已超出限制，最多支持添加 40 位同行人' }
      }
      // 检查同行人数据是否完整
      if (!companionValidation.isValid) {
        return { valid: false, message: companionValidation.message || '同行人信息填写不完整' }
      }

      // 新增：人员重复校验（申请人 vs 同行人、同行人之间）
      const participantValidation = validateCompanions(
        basicInfo.applicantId,
        formData.companions.map(c => ({
          employeeId: c.nameId || '',
          employeeName: c.name
        }))
      )
      if (!participantValidation.isValid) {
        return { valid: false, message: participantValidation.message }
      }
    }

    return { valid: true }
  }

  /**
   * 转换同行人数据格式（前端 → 后端）
   */
  const buildCompanionRequests = (companions: FormCompanion[]): CompanionRequest[] => {
    return (companions || []).map((c: FormCompanion) => ({
      employeeId: c.nameId || '',
      name: c.name,
      departmentId: c.department,
      departmentName: c.departmentName || '',
      estimatedAmount: c.estimatedAmount || formData.estimatedExpense || 0,
    }))
  }

  /**
   * 转换行程段数据格式（前端 → 后端）
   * arrivalCity → destinationCity, purpose → reason
   */
  const buildTripSegmentRequests = (segments: FormTripSegment[]): TripSegmentRequest[] => {
    return (segments || []).map((segment: FormTripSegment) => ({
      startDate: formatDateToISO(segment.startDate),
      endDate: formatDateToISO(segment.endDate),
      departureCity: segment.departureCity,
      departureCityCode: segment.departureCityCode,
      destinationCity: segment.arrivalCity,
      destinationCityCode: segment.arrivalCityCode,
      reason: segment.purpose,
    }))
  }

  /**
   * 计算多段行程的汇总日期和城市
   */
  const computeMultiSegmentSummary = (
    tripSegments: TripSegmentRequest[]
  ): { startDate: string; endDate: string; departureCity: string; departureCityCode: string; destinationCity: string; destinationCityCode: string } => {
    if (tripSegments.length === 0) {
      return {
        startDate: formData.startDate,
        endDate: formData.endDate,
        departureCity: formData.departureCity,
        departureCityCode: formData.departureCityCode,
        destinationCity: formData.destinationCity,
        destinationCityCode: formData.destinationCityCode,
      }
    }

    const startDate = tripSegments.reduce((earliest: string, seg: TripSegmentRequest) => {
      const segDate = new Date(seg.startDate)
      const earlyDate = new Date(earliest)
      return segDate < earlyDate ? seg.startDate : earliest
    }, tripSegments[0].startDate)

    const endDate = tripSegments.reduce((latest: string, seg: TripSegmentRequest) => {
      const segDate = new Date(seg.endDate)
      const lateDate = new Date(latest)
      return segDate > lateDate ? seg.endDate : latest
    }, tripSegments[0].endDate)

    const first = tripSegments[0]
    const last = tripSegments[tripSegments.length - 1]

    return {
      startDate,
      endDate,
      departureCity: first.departureCity,
      departureCityCode: first.departureCityCode || '',
      destinationCity: last.destinationCity,
      destinationCityCode: last.destinationCityCode || '',
    }
  }

  /**
   * 构建多段行程的合并 reason
   */
  const buildMultiSegmentReason = (): string => {
    if (category !== 'CROSS_CITY' || !formData.multiSegment || !formData.tripSegments || formData.tripSegments.length === 0) {
      return formData.reason || ''
    }

    return formData.tripSegments
      .map((seg: FormTripSegment, idx: number) => `行程${idx + 1}: ${seg.purpose || ''}`)
      .filter((p: string) => p.trim() !== `行程${0}: `)
      .join('\n') || '多段行程出差'
  }

  /**
   * 构建完整的请求数据
   *
   * [DEFERRED:multi-segment] tripSegments 仅在 multiSegment=true 时发送；
   * 单段模式传 null，后端见 null 跳过 mergeSegments，不会删除历史行程段数据。
   * 恢复多段支持时：删除下方 null 分支，直接用 buildTripSegmentRequests 的结果即可。
   */
  const buildRequestData = (): CreateDraftRequest => {
    const companions = buildCompanionRequests(formData.companions)

    // [DEFERRED:multi-segment] 单段模式不发送 tripSegments，避免后端误删历史行程段
    const tripSegments = formData.multiSegment
      ? buildTripSegmentRequests(formData.tripSegments)
      : null

    let startDate = formData.startDate
    let endDate = formData.endDate
    let departureCity = formData.departureCity
    let departureCityCode = formData.departureCityCode
    let destinationCity = formData.destinationCity
    let destinationCityCode = formData.destinationCityCode

    if (formData.multiSegment && tripSegments && tripSegments.length > 0) {
      const summary = computeMultiSegmentSummary(tripSegments)
      startDate = summary.startDate
      endDate = summary.endDate
      departureCity = summary.departureCity
      departureCityCode = summary.departureCityCode
      destinationCity = summary.destinationCity
      destinationCityCode = summary.destinationCityCode
    }

    const reason = buildMultiSegmentReason()

    return {
      category,
      expenseType: formData.expenseType,
      startDate: formatDateToISO(startDate),
      endDate: formatDateToISO(endDate),
      reason,
      location: formData.location || '',
      departureCity,
      departureCityCode,
      destinationCity,
      destinationCityCode,
      // 多目的城市：跨城单段时传 destinationCities；多段行程不传（由 tripSegments 处理）
      destinationCities: (!formData.multiSegment && formData.destinationCities && formData.destinationCities.length > 0)
        ? formData.destinationCities
        : undefined,
      estimatedExpense: formData.estimatedExpense ?? undefined,
      isMultiSegment: formData.multiSegment,
      needsTrainTravel: formData.trainNeeded || false,
      showInLingxi: formData.showInLingxi,
      companions,
      tripSegments,
      applicantId: basicInfo.applicantId,
      applicantName: basicInfo.applicantName,
      applicantDeptId: basicInfo.applicantDeptId,
      applicantDepartmentName: basicInfo.applicantDepartment,
    }
  }

  // 自动保存 Hook（FR-027）- 必须在 validateForm 和 buildRequestData 定义之后调用
  const { markAsSaved, cancelPendingSaves, resumeAutoSave } = useAutoSave({
    formData,
    savedDraftId,
    canSaveDraft,
    buildCreateDraftRequest: buildRequestData,
    onSaveSuccess: (id) => {
      setSavedDraftId(id)
      // 新建模式下首次自动保存成功后，静默将 URL 更新为编辑路由
      // 使用 replaceState 而非 navigate，避免组件重新挂载导致表单数据丢失
      // 此后刷新页面将直接进入编辑模式，不会重复创建草稿
      if (mode === 'create') {
        window.history.replaceState(null, '', `/travel/${id}/edit`)
      }
      // 静默保存，不显示提示
    },
    onSaveError: () => {
      // 静默失败，不显示提示
    },
  })

  /**
   * 加载申请数据（用于编辑和查看模式）
   */
  useEffect(() => {
    const loadApplicationData = async () => {
      // 只在编辑或查看模式下加载数据
      if ((mode === 'edit' || mode === 'view') && applicationId) {
        setIsLoading(true)
        try {
          const response = await apiClient.getTravelApplication(applicationId)
          const data = response.data

          if (!data) {
            Modal.error({
              title: '加载失败',
              content: '未找到该申请',
              onOk: () => navigate('/dashboard'),
            })
            return
          }

          // 回填基础信息，实时查询申请人 collection（避免存快照导致过期）
          let applicantCollection: string | undefined = undefined
          if (data.applicantId) {
            try {
              const emp = await apiClient.getEmployeeByDomain(data.applicantId)
              applicantCollection = emp?.collection || undefined
            } catch {
              // collection 查询失败不影响主流程
            }
          }
          setBasicInfo({
            applicantId: data.applicantId || '',
            applicantName: data.applicantName || '',
            applicantDisplayNumber: '',  // 后端不返回工号，降级展示
            applicantDeptId: data.applicantDepartmentId || '',
            applicantDepartment: data.applicantDepartmentName || '',
            applicantCollection,
          })

          // 回填出差类别
          const travelCategory = data.category as TravelCategory
          setCategory(travelCategory)

          // 转换同行人数据格式（后端 → 前端）
          const companions: FormCompanion[] = (data.companions || []).map((c) => {
            const asResponse = c as unknown as TravelCompanionResponse
            return {
              id: c.id,
              nameId: asResponse.employeeId || (c as TravelCompanion).nameId || '',
              name: c.name,
              department: asResponse.departmentId || (c as TravelCompanion).department || '',
              departmentName: c.departmentName,
              estimatedAmount: c.estimatedAmount || 0,
            }
          })

          // 转换行程段数据格式（后端 → 前端）
          const tripSegments: FormTripSegment[] = (data.tripSegments || []).map((seg: TripSegmentResponse, index: number) => ({
            sequence: seg.orderIndex || (index + 1),
            startDate: parseDateFromBackend(seg.startDate),
            endDate: parseDateFromBackend(seg.endDate),
            departureCity: seg.departureCity,
            departureCityCode: seg.departureCityCode || '',
            arrivalCity: seg.destinationCity,
            arrivalCityCode: seg.destinationCityCode || '',
            purpose: seg.reason || '',
          }))

          // 回填表单数据
          const loadedFormData: TravelFormState = {
            category: travelCategory,
            expenseType: (data.expenseType as ExpenseType) || 'DEPARTMENT',
            startDate: parseDateFromBackend(data.startDate),
            endDate: parseDateFromBackend(data.endDate),
            reason: data.reason || '',
            location: data.location || '',
            departureCity: data.departureCity || '',
            departureCityCode: (data as any).departureCityCode || '',
            destinationCity: data.destinationCity || '',
            destinationCityCode: (data as any).destinationCityCode || '',
            // 多目的城市回填：优先用 destinationCities，存量数据降级构造单元素数组
            destinationCities: (() => {
              const cities = (data as any).destinationCities
              if (cities && cities.length > 0) return cities
              if (data.destinationCity) return [{ name: data.destinationCity, code: (data as any).destinationCityCode || '' }]
              return []
            })(),
            companions: companions,
            hasCompanion: companions.length > 0,
            estimatedExpense: data.applicantEstimatedAmount ?? null,  // 使用申请人预估费用
            showInLingxi: data.showInLingxi !== false,
            multiSegment: data.isMultiSegment || false,
            tripSegments: tripSegments,
            trainNeeded: data.needsTrainTravel || false,
          }
          setFormData(loadedFormData)

          // 设置已保存的草稿ID
          setSavedDraftId(data.id)

          // 记录申请状态和审批意见，用于顶部状态 Banner
          setLoadedStatus(data.status || null)
          setLoadedApprovalOpinion(data.approvalOpinion || null)

          // 数据加载完成后，计算跨城行程的出差时长
          if (travelCategory === 'CROSS_CITY') {
            if (data.isMultiSegment && tripSegments.length > 0) {
              // 多段行程：各段本地计算后求和
              const allSegmentsHaveTime = tripSegments.every(seg => seg.startDate && seg.endDate)
              if (allSegmentsHaveTime) {
                const totalLength = tripSegments.reduce((sum, seg) => {
                  return sum + calcCrossCityHours(seg.startDate!, seg.endDate!)
                }, 0)
                setBusinessLength(totalLength)
              }
            } else if (!data.isMultiSegment) {
              // 单段行程：直接本地计算
              const startDate = parseDateFromBackend(data.startDate)
              const endDate = parseDateFromBackend(data.endDate)
              if (startDate && endDate) {
                setBusinessLength(calcCrossCityHours(startDate, endDate))
              }
            }
          }

          // 已提交的申请：拉取真实审批链（含每节点 operate/time）
          const nonDraftStatuses = ['APPROVING', 'APPROVED', 'REJECTED', 'WITHDRAWN', 'TERMINATED']
          if (data.status && nonDraftStatuses.includes(data.status) && data.id) {
            setIsProcessChainLoading(true)
            apiClient.getProcessChain(String(data.id))
              .then(res => { setProcessChainNodes(res.data?.nodes ?? []) })
              .catch(() => { /* 静默降级，不影响主流程 */ })
              .finally(() => { setIsProcessChainLoading(false) })
          }

          // 同步 useAutoSave 的数据快照，防止加载后立即触发自动保存
          markAsSaved(loadedFormData)

          // 记录数据快照
          recordSavedData()

        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : '加载申请数据时发生错误'
          console.error('加载申请数据失败:', error)
          Modal.error({
            title: '加载失败',
            content: errorMessage,
            onOk: () => navigate('/dashboard'),
          })
        } finally {
          setIsLoading(false)
        }
      }
    }

    loadApplicationData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, applicationId])  // 🔑 移除 navigate 依赖，避免重复调用

  /**
   * 提交申请 - 打开二次确认对话框
   */
  const handleSubmit = async () => {
    const validation = validateForm()
    if (!validation.valid) {
      Modal.warning({
        title: '表单验证失败',
        content: validation.message,
      })
      return
    }
    // 新增代码 - 打开 SubmitConfirmDialog 替代 Modal.confirm
    setShowSubmitDialog(true)
  }

  /**
   * 新增代码 - 构建提交摘要数据（供 SubmitConfirmDialog 展示）
   *
   * 多段行程时从 tripSegments 提取出发城市、目的城市、开始日期、结束日期：
   * - departureCity: 第一段行程的出发城市
   * - destinationCity: 最后一段行程的到达城市（arrivalCity）
   * - startDate: 第一段行程的开始日期
   * - endDate: 最后一段行程的结束日期
   */
  const buildSubmitSummary = (): SubmitSummary => {
    let departureCity = formData.departureCity || ''
    let destinationCity = formData.destinationCity || ''
    let startDate = formData.startDate || ''
    let endDate = formData.endDate || ''

    // 多段行程时，从 tripSegments 提取城市和时间信息
    if (formData.multiSegment && formData.tripSegments && formData.tripSegments.length > 0) {
      const segments = formData.tripSegments
      departureCity = segments[0]?.departureCity || ''
      destinationCity = segments[segments.length - 1]?.arrivalCity || ''
      startDate = segments[0]?.startDate || ''
      endDate = segments[segments.length - 1]?.endDate || ''
    }

    return {
      applicantName: basicInfo.applicantName || '—',
      category: category as TravelCategory,
      expenseType: formData.expenseType,
      startDate,
      endDate,
      departureCity,
      destinationCity,
      // 多目的城市：跨城单段时传入，多段行程不传
      destinationCities: (!formData.multiSegment && formData.destinationCities && formData.destinationCities.length > 0)
        ? formData.destinationCities
        : undefined,
      location: formData.location,
      trainNeeded: formData.trainNeeded,
      companionCount: (formData.companions || []).length,
      totalExpense: (formData.estimatedExpense ?? 0) +
        (formData.companions || []).reduce((sum, c) => sum + (Number(c.estimatedAmount) || 0), 0),
    }
  }

  /**
   * 新增代码 - 确认提交后的实际提交逻辑
   */
  const executeSubmit = async () => {
    // 取消所有待执行的自动保存，防止提交后触发"自动保存失败"提示
    cancelPendingSaves()
    setIsSubmitting(true)
    try {
      let draftId = savedDraftId

      // 如果没有草稿ID，先创建草稿
      if (!draftId) {
        const requestData = buildRequestData()
        const draftResponse = await apiClient.createDraft(requestData)
        draftId = draftResponse.data?.id
        setSavedDraftId(draftId)
      } else {
        // 如果已有草稿ID，提交前先保存一次，确保最新修改被持久化
        const requestData = buildRequestData()
        await apiClient.updateDraft(Number(draftId), requestData)
      }

      // 构建提交请求数据
      const submitRequestData = buildRequestData()

      // 提交申请
      const response = await apiClient.submitTravelApplication(String(draftId!), submitRequestData)

      // 检查业务逻辑是否成功
      if (response.data?.success === false) {
        // 业务失败（如时间冲突、预算不足等）
        const errorContent = response.data?.autoSaved
          ? `${response.data?.message || '提交失败'}\n\n数据已自动保存，您可以修改后重新提交`
          : response.data?.message || '提交申请时发生错误，请检查后重试'

        setShowSubmitDialog(false)
        Modal.error({
          title: '提交失败',
          content: errorContent,
        })

        // 如果数据已自动保存，更新数据快照
        if (response.data?.autoSaved) {
          recordSavedData()
        }
        resumeAutoSave()
        return
      }

      // 业务成功
      recordSavedData()
      setShowSubmitDialog(false)
      Modal.success({
        title: '提交成功',
        content: response.data?.message || '申请已提交，请关注审批进度',
        onOk: () => {
          navigate('/dashboard')
        },
      })
    } catch (error: unknown) {
      resumeAutoSave()
      const errorMessage = error instanceof Error ? error.message : '提交申请时发生错误，请稍后重试'
      console.error('提交申请失败:', error)
      setShowSubmitDialog(false)
      Modal.error({
        title: '提交失败',
        content: errorMessage,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`w-full space-y-6 ${mode === 'view' ? 'view-mode' : ''}`}>
      {/* 加载状态 */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-lg">加载中...</span>
            </div>
          </div>
        </div>
      )}

      {/* 主容器 - 在 MainLayout 的 padding 内渲染 */}
      <div className="mx-auto w-full max-w-6xl space-y-6">
        {/* 面包屑导航 */}
        <Breadcrumb
          items={[
            {
              title: <span className="cursor-pointer" onClick={() => navigate('/dashboard')}>出差申请列表</span>,
            },
            {
              title: mode === 'create' ? '新建申请' : mode === 'edit' ? '编辑申请' : '查看申请',
            },
          ]}
        />
        {/* 审批结果 Banner：被拒绝或已撤回时在表单顶部提示原因 */}
        {loadedStatus === 'REJECTED' && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-700 mb-0.5">审批已拒绝</p>
              <p className="text-sm text-red-600">
                  {loadedApprovalOpinion
                  ? <><span className="font-medium">拒绝原因：</span>{loadedApprovalOpinion}</>
                  : <span className="text-red-400 italic">（未收到拒绝原因，请在百川审批系统查看详情）</span>}
              </p>
              {(mode === 'edit') && (
                <p className="text-xs text-red-500 mt-1">修改后可重新提交申请。</p>
              )}
            </div>
          </div>
        )}

        {loadedStatus === 'WITHDRAWN' && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-700 mb-0.5">申请已撤回</p>
              <p className="text-sm text-amber-600">
                  {loadedApprovalOpinion
                  ? <><span className="font-medium">撤回说明：</span>{loadedApprovalOpinion}</>
                  : <span className="text-amber-400 italic">（未收到撤回说明，请在百川审批系统查看详情）</span>}
              </p>
              {(mode === 'edit') && (
                <p className="text-xs text-amber-500 mt-1">修改后可重新提交申请。</p>
              )}
            </div>
          </div>
        )}

        {loadedStatus === 'TERMINATED' && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-700 mb-0.5">申请已被终止</p>
              <p className="text-sm text-slate-600">
                {loadedApprovalOpinion
                  ? <><span className="font-medium">终止原因：</span>{loadedApprovalOpinion}</>
                  : <span className="text-slate-400 italic">（管理员终止，如有疑问请联系审批负责人）</span>}
              </p>
              
            </div>
          </div>
        )}

        {/* 第一栏：基础信息 */}
        <div className="clay-card" style={{ position: 'relative', zIndex: 30, overflow: 'visible' }}>
          <h3 className="heading-1 mb-clay-lg">
            基础信息
          </h3>
          <BasicInfoSection
            basicInfo={basicInfo}
            onChange={setBasicInfo}
            isApplicantReadonly={mode !== 'view'}
            disabled={mode === 'view'}
          />
        </div>

        {/* 第二栏：出差类别 + 出差类型（跨城时显示） */}
        <div className="clay-card relative z-10">
          <h3 className="heading-1 mb-clay-lg">
            出行类别
          </h3>
          <TravelCategorySelector
            value={category}
            onChange={handleCategoryChange}
            disabled={mode === 'view'}
          />

          {/* 出差类型选择器 - 仅跨城时显示 */}
          {category === 'CROSS_CITY' && (
            <div className="mt-clay-lg">
              <ExpenseTypeSelector
                value={formData.expenseType}
                onChange={(expenseType: ExpenseType) => {
                  handleFormChange({
                    expenseType,
                    ...(expenseType === 'SELF_PAID' ? { estimatedExpense: 0 } : {}),
                  })
                }}
                disabled={mode === 'view'}
              />
            </div>
          )}
        </div>

        {/* 第三部分栏：动态表单 - 根据出差类别显示不同表单 */}
        {category === 'LOCAL' && (
          <div className="clay-card">
            <h3 className="heading-1 mb-clay-lg">
              同城-出行详情
            </h3>
            {/* 占用时段冲突提示和排班警告仅在可重新提交的状态下有意义；
                APPROVING/APPROVED 状态用户无法修改日期重提，不展示 */}
            {(() => {
              const isResubmittable = !loadedStatus || ['DRAFT', 'REJECTED', 'WITHDRAWN'].includes(loadedStatus)
              return (
                <LocalTripForm
                  formData={formData as unknown as LocalTripFormData}
                  onChange={handleFormChange}
                  disabled={mode === 'view'}
                  occupiedRanges={isResubmittable ? occupiedRanges : []}
                  hasScheduling={isResubmittable ? hasScheduling : undefined}
                  endDateHasScheduling={isResubmittable ? endDateHasScheduling : undefined}
                  businessLength={isResubmittable ? businessLength : undefined}
                />
              )
            })()}
          </div>
        )}

        {category === 'CROSS_CITY' && (
          <div className="clay-card" style={{ position: 'relative', zIndex: 15, overflow: 'visible' }}>
            <h3 className="heading-1 mb-clay-lg">
              跨城-出差详情
            </h3>
            <CrossCityTripForm
              key={applicationId ?? 'new'}
              formData={formData as unknown as CrossCityTripFormData}
              onChange={handleFormChange}
              disabled={mode === 'view'}  // 🔑 查看模式禁用所有字段
            />
          </div>
        )}

        {/* 第四栏：同行人控制 - 仅在跨城时显示 */}
        {category === 'CROSS_CITY' && (
          <div className="clay-card" style={{ position: 'relative', zIndex: 20, overflow: 'visible' }}>
            <h3 className="heading-1 mb-clay-lg">
              同行人员
            </h3>

            {/* 是否有同行人选择框 */}
            <div className="mb-clay-lg required-field">
              <label className="heading-2 mb-clay-sm block">是否有同行人 <span className="text-red-500">*</span></label>
              {isMobile ? (
                <>
                  <div
                    className={`clay-input w-full flex items-center justify-between ${mode === 'view' ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => { if (mode !== 'view') setHasCompanionPickerVisible(true) }}
                  >
                    <span>{formData.hasCompanion ? '是' : '否'}</span>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <Picker
                    columns={[[{ label: '否', value: 'no' }, { label: '是', value: 'yes' }]]}
                    visible={hasCompanionPickerVisible}
                    onClose={() => {
                      setHasCompanionPickerVisible(false)
                      document.body.style.overflow = ''
                    }}
                    value={[formData.hasCompanion ? 'yes' : 'no']}
                    onConfirm={v => handleFormChange({
                      hasCompanion: v[0] === 'yes',
                      companions: v[0] === 'yes' ? formData.companions : [],
                    })}
                  />
                </>
              ) : (
                <select
                  className="clay-input w-full appearance-none"
                  value={formData.hasCompanion ? 'yes' : 'no'}
                  disabled={mode === 'view'}
                  onChange={(e) =>
                    handleFormChange({
                      hasCompanion: e.target.value === 'yes',
                      // ✅ 当选择"否"时，清空同行人数据
                      companions: e.target.value === 'yes' ? formData.companions : [],
                    })
                  }
                >
                  <option value="no">否</option>
                  <option value="yes">是</option>
                </select>
              )}
            </div>

            {/* 同行人列表 - 仅当 hasCompanion = true 时显示 */}
            {formData.hasCompanion && (
              <CompanionList
                companions={formData.companions}
                onChange={(companions) =>
                  handleFormChange({ companions })
                }
                onValidationChange={handleCompanionValidationChange}
                applicantDeptId={basicInfo.applicantDeptId}
                applicantCollection={basicInfo.applicantCollection}
                disabled={mode === 'view'}
              />
            )}
          </div>
        )}

        {/* 新增代码 - 预算检查结果展示 (FR-008, FR-009) - 暂时禁用 */}
        {/* {category === 'CROSS_CITY' && formData.expenseType === 'DEPARTMENT' && (
          <BudgetCheckResult
            results={budgetResults}
            isChecking={isBudgetChecking}
            isExceeded={isBudgetExceeded}
          />
        )} */}

        {/* 底部：审批预览和操作按钮 */}
        {category && (
          <>
            <ApprovalPreview
              category={category}
              estimatedExpense={formData.estimatedExpense ?? undefined}
              nodes={mode !== 'edit' && loadedStatus && ['APPROVING', 'APPROVED', 'REJECTED', 'WITHDRAWN', 'TERMINATED'].includes(loadedStatus)
                ? processChainNodes
                : approvalNodes}
              isLoading={mode !== 'edit' && loadedStatus && ['APPROVING', 'APPROVED', 'REJECTED', 'WITHDRAWN', 'TERMINATED'].includes(loadedStatus)
                ? isProcessChainLoading
                : isApprovalLoading}
              error={approvalError}
              isActual={!!(mode !== 'edit' && loadedStatus && ['APPROVING', 'APPROVED', 'REJECTED', 'WITHDRAWN', 'TERMINATED'].includes(loadedStatus))}
              applicationStatus={loadedStatus}
            />

            {/* Phase 4 US3 — 携程商旅引导文字：仅在 view 模式 + APPROVED + 跨城 + 部门承担时展示 */}
            {mode === 'view'
              && loadedStatus === 'APPROVED'
              && category === 'CROSS_CITY'
              && formData.expenseType === 'DEPARTMENT'
              && <CtripGuideText />
            }

            {/* 只在创建和编辑模式显示操作按钮 */}
            {mode !== 'view' && (
              <div className="flex flex-col sm:flex-row gap-clay-md justify-end pb-clay-2xl">
                <button
                  className="clay-btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '提交中...' : '提交申请'}
                </button>
              </div>
            )}

            {/* 查看模式显示返回按钮 */}
            {mode === 'view' && (
              <div className="flex justify-end pb-clay-2xl">
                <button
                  className="clay-btn-ghost"
                  onClick={() => navigate('/dashboard')}
                >
                  返回列表
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 新增代码 - 二次确认对话框 (FR-012, FR-012A) */}
      {category && (
        <SubmitConfirmDialog
          visible={showSubmitDialog}
          summary={buildSubmitSummary()}
          loading={isSubmitting}
          onConfirm={executeSubmit}
          onCancel={() => setShowSubmitDialog(false)}
        />
      )}
    </div>
  )
}

export default TravelApplicationForm
